import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiHome, FiList, FiLogOut, FiMenu, FiX, FiCpu, FiUsers, FiGitBranch, FiClock } from 'react-icons/fi';
import InstallBanner from './InstallBanner';

const TechLayout = ({ children }) => {
  const { user, logout, isTechAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/tech-login');
  };

  const nav = [
    { path: '/tech', icon: FiHome, label: 'Home' },
    { path: '/tech/tasks', icon: FiList, label: 'Tickets' },
    { path: '/tech/timesheet', icon: FiClock, label: 'Timesheet' },
    { path: '/tech/pipeline', icon: FiGitBranch, label: 'Pipeline' },
    ...(isTechAdmin ? [{ path: '/tech/users', icon: FiUsers, label: 'Users' }] : []),
  ];

  const isActive = (path) =>
    path === '/tech' ? location.pathname === '/tech' : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pb-20 lg:pb-0">
      <header className="sticky top-0 z-40 bg-slate-950/90 backdrop-blur-md border-b border-slate-800">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-teal-500/20 text-teal-300 flex items-center justify-center">
              <FiCpu size={18} />
            </div>
            <div>
              <p className="text-sm font-bold text-white leading-tight">TaskMate Tech</p>
              <p className="text-[11px] text-slate-400 truncate max-w-[180px]">
                {user?.name}
                {isTechAdmin ? ' · Tech admin' : ''}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden p-2 text-slate-300 touch-manipulation"
          >
            {menuOpen ? <FiX size={22} /> : <FiMenu size={22} />}
          </button>
          <div className="hidden lg:flex items-center gap-2">
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-800 text-sm"
            >
              <FiLogOut size={16} />
              Logout
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="lg:hidden border-t border-slate-800 px-4 py-3 space-y-1">
            {nav.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-3 p-3 rounded-xl ${
                  isActive(item.path) ? 'bg-teal-500/20 text-teal-200' : 'text-slate-300'
                }`}
              >
                <item.icon size={18} />
                {item.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center gap-3 p-3 rounded-xl text-red-300"
            >
              <FiLogOut size={18} />
              Logout
            </button>
          </div>
        )}
      </header>

      <div className="lg:flex max-w-6xl mx-auto">
        <aside className="hidden lg:block w-52 shrink-0 p-4 space-y-1">
          {nav.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium ${
                isActive(item.path)
                  ? 'bg-teal-500/20 text-teal-200'
                  : 'text-slate-400 hover:bg-slate-900 hover:text-white'
              }`}
            >
              <item.icon size={18} />
              {item.label}
            </Link>
          ))}
        </aside>
        <main className="flex-1 px-4 pt-4 pb-6 lg:p-6 min-w-0">
          <InstallBanner />
          {children}
        </main>
      </div>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-slate-900/95 backdrop-blur-md border-t border-slate-800 safe-area-pb">
        <div className="flex">
          {nav.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex-1 flex flex-col items-center py-3 text-xs touch-manipulation ${
                isActive(item.path) ? 'text-teal-300' : 'text-slate-500'
              }`}
            >
              <item.icon size={22} />
              <span className="mt-1">{item.label}</span>
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default TechLayout;
